import {
  SkyBadge,
  SkyDivider,
  SkyDrawer,
  SkyMessageBar,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import type { ConnectorAdminView, ConnectorUsageView } from "@shared/types/connectorsAdmin";
import {
  ConnectorUsageBody,
  deactivateBlockedReason,
  useConnectorUsage,
} from "@/components/platform/ConnectorUsageCard";
import { ConnectorFieldPreview } from "@/components/platform/ConnectorFieldPreview";
import { EMPTY_CELL } from "@/components/platform/format";
import { useConnectorSeedBlocks } from "@/hooks/use-connectors-admin";
import { egressDetailText } from "@/lib/egressText";
import { InlineErrorBar, type InlineFailure } from "@/lib/feedback/InlineErrorBar";
import { formatInstant } from "@/lib/time/displayTime";
import { cancelledByWording } from "@/components/runs/runFormatting";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * One connector definition, read (FR-30, FR-33, canvas 13 panel 02).
 *
 * The drawer reads TWO routes because the definition is split across them:
 * `/platform/connectors/:name` carries the run tunables, the image tags and the
 * seed checksum, and `/connectors/:name` carries the seed's own field blocks,
 * which the admin view leaves out because they are large. The second is
 * optional — it needs an instance context — so the panel renders in full
 * without it and says what is missing rather than failing as a whole.
 */

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <SkyText size={200} color="muted" block>
        {label}
      </SkyText>
      <SkyText size={200} block wrap>
        {value}
      </SkyText>
    </div>
  );
}

function hours(seconds: number): string {
  if (seconds % 3600 === 0) return `${seconds / 3600} h`;
  if (seconds % 60 === 0) return `${seconds / 60} min`;
  return `${seconds} s`;
}

/** "Supported · go back 3 days · per entity", or the connector's lack of support. */
function incrementalText(connector: ConnectorAdminView): string {
  if (!connector.isIncrementalSupported) return "Not supported by this connector";
  const goBack =
    connector.incrementalGoBackDays === null
      ? ""
      : ` · go back ${connector.incrementalGoBackDays} days`;
  const perEntity = connector.supportsIncrementalPerEntity ? " · per entity" : "";
  return `Supported${goBack}${perEntity}`;
}

/**
 * #1372: "Tables" is the tables selected across the connector's connections, the number each
 * connection's page shows. Never the seed's declared list (a connector that discovers its tables
 * declares none). Until the usage is read, or when its read failed, the count is not known and
 * reads as an empty cell. An instance that could not be read leaves its tables out, so the count
 * is then a lower bound.
 */
function drawerTableCount(
  usage: Pick<ConnectorUsageView, "rows" | "unreadable"> | undefined,
): string {
  if (!usage) return EMPTY_CELL;
  const total = usage.rows.reduce((sum, row) => sum + row.selectedEntityCount, 0);
  return usage.unreadable.length > 0 ? `At least ${total}` : `${total}`;
}

/** The definition's facts: image, tunables, incremental support, egress, seed, last change. Tables comes from the usage read. */
function ConnectorFacts({
  connector,
  zone,
  usage,
}: {
  connector: ConnectorAdminView;
  zone: string;
  usage: ConnectorUsageView | undefined;
}) {
  // #2478: the server's label as it reads mid-sentence ("by the system", "by a removed user").
  const changedBy = connector.updatedByName
    ? ` by ${cancelledByWording(connector.updatedByName)}`
    : "";
  return (
    <div className="grid grid-cols-2 gap-4">
      <Fact label="Image" value={connector.imageName ?? EMPTY_CELL} />
      <Fact label="Kind" value={connector.kind} />
      <Fact
        label="Run tunables"
        value={`${connector.cpu} CPU · ${connector.memoryGb} GB · timeout ${hours(connector.runTimeoutSeconds)} · poll ${connector.pollIntervalSeconds} s · ${connector.maxConcurrentRuns} concurrent`}
      />
      <Fact label="Incremental" value={incrementalText(connector)} />
      <Fact label="Egress" value={egressDetailText(connector.egressFqdns)} />
      <Fact
        label="Seed checksum"
        value={
          connector.seedChecksum
            ? `${connector.seedChecksum.slice(0, 12)}…`
            : "never seeded from a file"
        }
      />
      <Fact label="Tables" value={drawerTableCount(usage)} />
      <Fact
        label="Last changed"
        value={`${formatInstant(connector.updatedAt, zone, "short", { named: true })}${changedBy}`}
      />
    </div>
  );
}

export function ConnectorDetailDrawer({
  open,
  onOpenChange,
  connector,
  hasInstanceContext,
  onOpenVersions,
  onToggleActive,
  isTogglingActive,
  toggleError = null,
  availableForNew = true,
  onToggleAvailable,
  isTogglingAvailable = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connector: ConnectorAdminView | null;
  /** The field preview needs an instance header; without one it is not fetched. */
  hasInstanceContext: boolean;
  onOpenVersions: () => void;
  onToggleActive: () => void;
  isTogglingActive: boolean;
  /**
   * Session 10 (ux-s10 §3): a refused Activate/Deactivate, said inside the drawer, which stays
   * open with the button that failed.
   */
  toggleError?: InlineFailure | null;
  /**
   * T-51: whether new connections in any tenant may use this connector ("Not available" for a
   * deprecated or preview one). Existing connections keep working either way.
   */
  availableForNew?: boolean;
  onToggleAvailable?: () => void;
  isTogglingAvailable?: boolean;
}) {
  const zone = useDisplayTz();
  const blocks = useConnectorSeedBlocks(
    connector?.name ?? null,
    open && hasInstanceContext && connector !== null,
  );

  // #1086: Deactivate is off while connections use the connector (or usage is still loading),
  // with the reason said above the facts. The server refuses it too (`CONNECTOR_IN_USE`).
  const usage = useConnectorUsage(connector?.name ?? "", open && connector !== null);

  if (!connector) return null;

  const deactivating = connector.isActive;
  const blockedReason = deactivating
    ? deactivateBlockedReason(connector.displayName, usage.data)
    : null;
  const deactivateOff = deactivating && (usage.isPending || blockedReason !== null);

  const defaultTag = connector.versions.find((version) => version.isDefault)?.tag ?? null;

  return (
    <SkyDrawer
      open={open}
      onOpenChange={onOpenChange}
      position="end"
      size="large"
      headerTitle={connector.displayName}
      subtitle={`${connector.name} · definition version ${connector.version} · pipeline template ${connector.pipelineTemplate} · runtime ${connector.runtime}`}
      testId="connector-drawer"
      footerButtons={[
        { label: "Image tags", appearance: "secondary", onClick: onOpenVersions },
        {
          label: connector.isActive ? "Deactivate" : "Activate",
          appearance: "outline",
          loading: isTogglingActive,
          disabled: deactivateOff,
          onClick: onToggleActive,
        },
        ...(onToggleAvailable
          ? [
              {
                label: availableForNew ? "Make not available" : "Make available",
                appearance: "outline" as const,
                loading: isTogglingAvailable,
                onClick: onToggleAvailable,
              },
            ]
          : []),
        { label: "Close", appearance: "subtle", onClick: () => onOpenChange(false) },
      ]}
    >
      <div className="flex flex-col gap-4">
        <InlineErrorBar
          error={toggleError}
          title={toggleError?.retryable ? undefined : "That change was not saved"}
          testId="connector-toggle-error"
        />
        {blockedReason ? (
          <SkyMessageBar
            intent="info"
            layout="multiline"
            title="This connector cannot be deactivated yet"
            message={blockedReason}
            testId="connector-deactivate-blocked"
          />
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <SkyBadge appearance="tint" color={connector.isActive ? "success" : "subtle"}>
            {connector.isActive ? "Active" : "Disabled"}
          </SkyBadge>
          {connector.isPreview ? (
            <SkyBadge appearance="outline" color="warning">
              Preview
            </SkyBadge>
          ) : null}
          {availableForNew ? null : (
            <SkyBadge appearance="outline" color="subtle">
              Not available for new connections
            </SkyBadge>
          )}
          {defaultTag ? (
            <SkyBadge appearance="outline" color="informative">
              default {defaultTag}
            </SkyBadge>
          ) : null}
        </div>

        <ConnectorFacts connector={connector} zone={zone} usage={usage.data} />

        {connector.description ? (
          <SkyText size={200} color="muted" block>
            {connector.description}
          </SkyText>
        ) : null}

        <SkyDivider />

        {/* W2-11 / B-27: where this connector is used, in the drawer and not only in the
            Deactivate refusal. Fetched while the drawer is open (the body mounts with it). */}
        <section
          aria-labelledby="connector-drawer-usage-title"
          data-testid="connector-drawer-usage"
        >
          <SkyText as="h3" id="connector-drawer-usage-title" size={300} weight="semibold" block>
            Used by
          </SkyText>
          <SkyText size={200} color="muted" block className="mb-2">
            Every connection that uses this connector, in every instance.
          </SkyText>
          {open ? <ConnectorUsageBody name={connector.name} /> : null}
        </section>

        <SkyDivider />

        <SkyText as="h3" size={300} weight="semibold" block>
          Fields
        </SkyText>
        {hasInstanceContext ? (
          <ConnectorFieldPreview
            fieldDefinitions={blocks.data?.fieldDefinitions}
            fieldGroups={blocks.data?.fieldGroups}
            isLoading={blocks.isPending}
            isError={blocks.isError}
          />
        ) : (
          <SkyText size={200} color="muted" block>
            Choose an instance to preview this definition's fields. The catalog read they come from
            is instance-scoped. Everything above is platform-scope and does not need one.
          </SkyText>
        )}
      </div>
    </SkyDrawer>
  );
}
